import React from 'react';
import { ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight } from 'lucide-react';

const PAGE_SIZE_OPTIONS = [10, 20, 30, 50, 100];

/**
 * Pagination — page navigator + page-size selector + result count.
 * Designed to be paired with the `usePaginated` hook.
 *
 *   <Pagination page={p.page} pageSize={p.pageSize} total={p.total}
 *               onPageChange={p.setPage} onPageSizeChange={p.setPageSize}
 *               start={p.start} end={p.end} />
 */
export default function Pagination({
  page,
  pageSize,
  total,
  start = 0,
  end = 0,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = PAGE_SIZE_OPTIONS,
  label = 'rows',
  className = '',
  compact = false,
}) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const canPrev = page > 1;
  const canNext = page < totalPages;
  const startDisp = total === 0 ? 0 : start + 1;
  const endDisp = end;

  // Window of visible page buttons: page +/- 2 with first & last
  const windowSize = 2;
  const pagesToShow = [];
  for (let i = Math.max(1, page - windowSize); i <= Math.min(totalPages, page + windowSize); i++) pagesToShow.push(i);
  if (!pagesToShow.includes(1)) pagesToShow.unshift(1);
  if (!pagesToShow.includes(totalPages)) pagesToShow.push(totalPages);
  const dedup = Array.from(new Set(pagesToShow)).sort((a, b) => a - b);

  return (
    <div className={`flex items-center justify-between gap-3 flex-wrap text-xs text-slate-600 ${className}`} data-testid="pagination-bar">
      <div className="flex items-center gap-2">
        <span>
          Showing <b className="text-slate-900">{startDisp.toLocaleString()}</b>
          {!compact && total > 0 ? <>–<b className="text-slate-900">{endDisp.toLocaleString()}</b></> : null}
          {' '}of <b className="text-slate-900">{total.toLocaleString()}</b> {label}
        </span>
      </div>

      <div className="flex items-center gap-2">
        <label className="flex items-center gap-1.5">
          <span className="text-[10px] uppercase tracking-wider font-display font-bold text-slate-500">Rows per page</span>
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="border border-slate-300 rounded-sm px-2 py-1 text-xs bg-white"
            data-testid="page-size-select"
          >
            {pageSizeOptions.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>

        <div className="inline-flex border border-slate-300 rounded-sm overflow-hidden">
          <NavBtn onClick={() => onPageChange(1)} disabled={!canPrev} testid="page-first"><ChevronsLeft size={13} /></NavBtn>
          <NavBtn onClick={() => onPageChange(page - 1)} disabled={!canPrev} testid="page-prev"><ChevronLeft size={13} /></NavBtn>
          {dedup.map((p, i) => {
            const prev = dedup[i - 1];
            const showGap = prev && p - prev > 1;
            return (
              <React.Fragment key={p}>
                {showGap && <span className="px-1.5 text-slate-300 select-none">…</span>}
                <button
                  type="button"
                  onClick={() => onPageChange(p)}
                  className={`min-w-[26px] px-2 py-1 text-xs border-l border-slate-300 font-display font-semibold transition-colors ${p === page ? 'bg-[#1F7A6E] text-white' : 'bg-white hover:bg-slate-50 text-slate-700'}`}
                  data-testid={`page-${p}`}
                >
                  {p}
                </button>
              </React.Fragment>
            );
          })}
          <NavBtn onClick={() => onPageChange(page + 1)} disabled={!canNext} testid="page-next" leftBorder><ChevronRight size={13} /></NavBtn>
          <NavBtn onClick={() => onPageChange(totalPages)} disabled={!canNext} testid="page-last" leftBorder><ChevronsRight size={13} /></NavBtn>
        </div>
      </div>
    </div>
  );
}

function NavBtn({ onClick, disabled, children, testid, leftBorder }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={`px-2 py-1 ${leftBorder ? 'border-l border-slate-300' : ''} bg-white hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed text-slate-700`}
    >
      {children}
    </button>
  );
}
